import { ArrowRight } from "lucide-react";
import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

// Anonymous Gregorian algorithm — computes Easter Sunday for a given year
function computeEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 1-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function getLiturgicalSeason(): { label: string; ref: string } | null {
  const now = new Date();
  const year = now.getFullYear();
  const easter = computeEaster(year);

  // Ash Wednesday = Easter - 46 days
  const ashWed = new Date(easter); ashWed.setDate(easter.getDate() - 46);
  // Pentecost = Easter + 49 days (end of Easter season)
  const pentecost = new Date(easter); pentecost.setDate(easter.getDate() + 49);
  // Advent: 4th Sunday before Dec 25
  const christmas = new Date(year, 11, 25);
  const adventStart = new Date(christmas);
  adventStart.setDate(christmas.getDate() - ((christmas.getDay() + 21) % 28 + 1));

  if (now >= adventStart) return { label: "This Advent", ref: "Isaiah 40:3" };
  if (now < new Date(year, 0, 7)) return { label: "This Epiphany", ref: "Matthew 2:2" };
  if (now >= ashWed && now < easter) return { label: "This Lent", ref: "Joel 2:12" };
  if (now >= easter && now < pentecost) return { label: "This Easter", ref: "Luke 24:6" };
  return null;
}

interface HeroSectionProps {
  onCTA: () => void;
}

// Deep dark brown — dramatically different from the cream opening above it
const DARK = "#1B1410";

export default function HeroSection({ onCTA }: HeroSectionProps) {
  const season = getLiturgicalSeason();

  return (
    <section
      id="hero-section"
      className="py-[160px] px-6 sm:px-10 relative overflow-hidden text-center"
      style={{ background: DARK }}
    >
      {/* Cross motifs — very subtle in the dark */}
      {[
        { top: "18%", left: "6%", size: 180 },
        { bottom: "15%", right: "5%", size: 130 },
      ].map((pos, i) => (
        <div
          key={i}
          className="absolute opacity-[0.04] hidden md:block"
          style={{ ...pos, width: pos.size, height: pos.size }}
        >
          <div className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2" style={{ background: C.cream }} />
          <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2" style={{ background: C.cream }} />
        </div>
      ))}

      <div className="max-w-[660px] mx-auto relative z-10">

        {/* Seasonal line — only when liturgically relevant */}
        {season && (
          <FadeIn delay={0}>
            <p
              className="font-heading italic mb-10"
              style={{ fontSize: 14, color: "rgba(250,248,243,0.50)", letterSpacing: "0.3px" }}
            >
              {season.label} &nbsp;·&nbsp; {season.ref}
            </p>
          </FadeIn>
        )}

        {/* The declaration */}
        <FadeIn delay={season ? 0.1 : 0}>
          <h1
            className="font-heading font-semibold"
            style={{
              fontSize: "clamp(46px, 8.5vw, 104px)",
              lineHeight: 1.04,
              letterSpacing: "-0.5px",
              color: C.cream,
              marginBottom: 0,
            }}
          >
            Where your words
            <br />
            meet scripture.
            <br />
            <em className="italic" style={{ color: "rgba(139,158,118,0.88)" }}>
              Every morning.
            </em>
          </h1>
        </FadeIn>

        {/* Utility subhead — anchors the metaphor */}
        <FadeIn delay={0.2}>
          <p
            className="font-heading italic mx-auto text-center"
            style={{
              fontSize: "clamp(16px, 1.9vw, 20px)",
              lineHeight: 1.7,
              color: "rgba(250,248,243,0.62)",
              maxWidth: 520,
              marginTop: 28,
            }}
          >
            A Christian journaling practice that meets your words with scripture — and a closing prayer shaped from what you actually wrote.
          </p>
        </FadeIn>

        {/* Thin cream rule */}
        <FadeIn delay={0.25}>
          <div
            className="mx-auto"
            style={{ width: 48, height: 1, background: "rgba(250,248,243,0.18)", margin: "40px auto" }}
          />
        </FadeIn>

        {/* What it actually is — three short phrases */}
        <FadeIn delay={0.35}>
          <p
            className="font-heading italic mx-auto"
            style={{
              fontSize: "clamp(17px, 2.1vw, 22px)",
              lineHeight: 1.7,
              maxWidth: 440,
              color: "rgba(250,248,243,0.58)",
            }}
          >
            Write freely. Receive scripture that responds to what you actually
            wrote. Close with a prayer shaped from your own words.
          </p>
        </FadeIn>

        {/* CTA */}
        <FadeIn delay={0.5}>
          <div className="mt-14 flex flex-col items-center gap-4">
            <button
              onClick={onCTA}
              className="font-ui font-medium border-none rounded-lg inline-flex items-center gap-2 cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
              style={{
                fontSize: 15,
                padding: "14px 38px",
                color: DARK,
                background: C.cream,
                boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
                letterSpacing: "0.3px",
              }}
              onMouseEnter={() => import("@/components/AuthModal")}
            >
              Begin Your Practice <ArrowRight size={15} />
            </button>
            <a
              href="#the-product"
              className="font-heading italic no-underline transition-opacity hover:opacity-80"
              style={{
                fontSize: 13,
                color: "rgba(250,248,243,0.55)",
                letterSpacing: "0.3px",
                marginTop: -4,
              }}
            >
              See how it works ↓
            </a>
            <div className="flex flex-col items-center" style={{ marginTop: 8 }}>
              <p
                className="font-heading italic"
                style={{ fontSize: 14, color: "rgba(250,248,243,0.38)", lineHeight: 1.5 }}
              >
                Ten mornings free. No credit card.
              </p>
              <p
                className="font-heading italic"
                style={{ fontSize: 12, color: "rgba(250,248,243,0.30)", lineHeight: 1.5, marginTop: 4 }}
              >
                Then $7/month — or stay on the free plan forever.
              </p>
            </div>
          </div>
        </FadeIn>

        {/* Trust — tradition band */}
        <FadeIn delay={0.7}>
          <div style={{ marginTop: 64 }}>
            <p
              className="font-ui text-center"
              style={{
                fontSize: 11,
                color: "rgba(250,248,243,0.28)",
                letterSpacing: "1.4px",
                marginBottom: 10,
              }}
            >
              BUILT FOR THE BREADTH OF CHRISTIAN TRADITION
            </p>
            <p
              className="font-heading italic text-center"
              style={{
                fontSize: 13,
                color: "rgba(250,248,243,0.42)",
                lineHeight: 1.6,
                marginBottom: 18,
                maxWidth: 480,
                margin: "0 auto 18px auto",
              }}
            >
              Includes the Deuterocanonical texts. Honors the liturgical calendar. Respects the Lectionary. Written for every tradition that calls Christ Lord.
            </p>
            <div className="flex justify-center gap-2 flex-wrap">
              {[
                "Roman Catholic",
                "Eastern Orthodox",
                "Anglican",
                "Lutheran",
                "Presbyterian",
                "Methodist",
                "Baptist",
                "Pentecostal",
                "Non-denominational",
              ].map((tradition) => (
                <span
                  key={tradition}
                  className="font-ui"
                  style={{
                    fontSize: 11,
                    color: "rgba(250,248,243,0.38)",
                    border: "1px solid rgba(250,248,243,0.12)",
                    borderRadius: 999,
                    padding: "5px 13px",
                  }}
                >
                  {tradition}
                </span>
              ))}
            </div>
          </div>
        </FadeIn>

      </div>
    </section>
  );
}
